import {
  AreaCoverageOption,
  ConstructibleAfterOption,
  ConstructionConditionOption,
  ConstructionPhaseOption,
  EfficiencyClassOption,
  EnergySourceOption,
  FlooringTypeOption,
  HeatingTypeOption,
  LocationClassificationTypeOption,
  ParkingSpaceTypeOption,
  SupplyTypeOption,
  UsageTypeOption,
} from './dropdown-types'
import { EnergyCertificateAvailability } from './radio-types'
import {
  Attachment,
  AttachmentData,
  BuildingEnergyRatingType,
  ContentStateInterface,
  EnergyCertificateCreationDate,
  Furnishing,
  Gender,
  GeoCoordinates,
  InsertionIdentification,
  InteriorQuality,
  ManualGeoCoding,
  Salutation,
  SmokingAllowed,
  TransactionType,
  YesNoOrByAppointment,
  YesNoOrNegotiable,
  YesOrNo,
} from './types'

export type RealEstate = {
  externalId?: string
  unitId?: string
  buildingId?: string
  realEstateId?: number
  id: string
  category: string
  transaction: TransactionType
  segmentation: string
  creationDate: string
  metaInformation?: RealEstateMetaInformation
  address: RealEstateAddress
  apiSearchData?: ApiSearchData
  title: string
  contact?: RealEstateContact
  additionalInformation?: RealEstateAdditionalInformation
  state?: string
  price?: RealEstatePrice
  courtageInformation?: RealEstateCourtageInformation
  otherCosts?: RealEstateOtherCost
  location?: RealEstateLocation
  buildingType?: string
  details?: RealEstateDetails
  information?: RealEstateInformation
  tenantDocuments?: RealEstateTenantDocuments
  returnUrl?: string
  context?: string
  attachments?: RealEstateAttachments[]
  objectName?: string
  published?: boolean
  tenantNetwork?: boolean
  conversionToLeadAllowed?: boolean
  groupNumber?: number
  mapImages?: MapImages
  publishInfo?: PublishInfo
}

export interface PublishInfo {
  rentPublished: boolean
  sellPublished: boolean
}

export interface ConvertedRealEstate {
  meta: InsertionIdentification
  listing: ContentStateInterface
  attachments: AttachmentData[]
  geoCoordinates?: GeoCoordinates
  manualAddress?: { manualGeoCoding?: ManualGeoCoding }
  category: string
  publishInfo?: PublishInfo
  tenantNetwork?: boolean
}

export interface RealEstateCourtageInformation {
  courtage?: string
  hasCourtage?: boolean
  courtageNote?: string
}

export type RealEstateOtherCost = {
  heatingCosts?: string
  heatingCostsOption?: string
  additionalCosts?: number
  deposit?: string
  totalRent?: number | string
}

export type RealEstateAttachments = {
  id: string
  offerApiId?: string
  offerApiRealEstateId?: number
  url?: string
  title: string
  contentType?: string
  creationDate: string
  source: string
  floorplan: boolean
  picture?: boolean
  document?: boolean
  valid?: boolean
  link?: boolean
  linkUrl?: string
  video?: boolean
  videoId?: string
}

export type EnergyCertificateType = {
  creationDate?: EnergyCertificateCreationDate
  buildingEnergyRatingType?: BuildingEnergyRatingType
  thermalCharacteristic?: number | string
  consumptionContainsWarmWater?: boolean
  efficiencyClass?: EfficiencyClassOption
  firingType?: EnergySourceOption[]
  legalConstructionYear?: string
}

export type RealEstateInformation = {
  condition?: ConstructionConditionOption
  lastRefurbishment?: number
  constructionYear?: number
  energyCertificateAvailability?: EnergyCertificateAvailability
  energyCertificate?: EnergyCertificateType
  constructionYearUnknown?: boolean
}

export type RealEstateGarageInformation = {
  parkingSpaceRent?: number | string
  noParkingSpaces?: number
  parkingSpaceType?: ParkingSpaceTypeOption
}

export type RealEstateRentDurations = {
  MONTHLY?: boolean
  YEARLY?: boolean
  WEEKLY?: boolean
  LONG_TERM?: boolean
}

export type RealEstateDetails = {
  garageInformation?: RealEstateGarageInformation
  siteRecommendedUseTypes?: ApiSiteRecommendedUseTypes
  noRooms?: string
  petsAllowed?: YesOrNo | YesNoOrNegotiable
  sharedGarden?: boolean
  noBathrooms?: string
  moveInDate?: string
  livingSpace?: string
  guestToilet?: boolean
  flatShareSuitable?: boolean
  equippedKitchen?: boolean
  certificateOfEligibilityNeeded?: boolean
  balconyTerrace?: boolean
  ramp?: boolean
  // Monument for store
  listed?: boolean
  powerSupply?: number
  liftingPlatform?: boolean
  floorLoad?: string
  craneValue?: string
  usableArea?: string
  lengthGarage?: string
  widthGarage?: string
  heightGarage?: string
  crane?: boolean
  plotArea?: string
  loadLifting?: boolean
  loadLiftingValue?: string
  goodsLift?: boolean
  goodsLiftLoad?: string
  basement?: boolean
  elevator?: boolean
  dishwasher?: boolean
  washingMachine?: boolean
  bathHasWc?: boolean
  bathHasShower?: boolean
  bathHasTub?: boolean
  internet?: boolean
  tv?: boolean
  summerResidencePractical?: boolean
  lodgerFlat?: boolean
  monument?: boolean
  nonSmoker?: boolean
  terrace?: boolean
  shortTermConstructible?: boolean
  buildingPermission?: boolean
  demolition?: boolean
  interiorQuality?: InteriorQuality
  heatingType?: HeatingTypeOption
  mainFloorSpace?: number | string
  freeFrom?: string
  freeUntil?: string
  startRentalDate?: string
  separableSpace?: number | string
  totalFloorSpace?: string
  handicappedAccessible?: boolean
  hasCanteen?: boolean
  kitchenComplete?: boolean
  highVoltage?: boolean
  lanCables?: YesNoOrByAppointment
  airConditioning?: YesNoOrByAppointment
  flooringType?: FlooringTypeOption
  rentDurations?: RealEstateRentDurations
  noFloors?: string // some real estate types have this as number, some as string
  floor?: number
  intermediateRent?: boolean
  minimumTermOfLease?: number
  requestedGender?: Gender
  flatShareSize?: number
  smokingAllowed?: SmokingAllowed
  furnishing?: Furnishing
  noRequestedFlatMates?: number
  ageOfRequestedFrom?: number
  ageOfRequestedTo?: number
  noMaleFlatMates?: number
  noFemaleFlatMates?: number
  ageOfFlatMatesFrom?: number
  ageOfFlatMatesTo?: number
  roomSize?: number | string
  rentSubsidy?: number | string
  grz?: number | string
  gfz?: number | string
  minRentalTime?: number
  maxRentalTime?: number
  maxNumberOfPersons?: number
  frontWindowLength?: number | string
  numberOfSeats?: number
  numberOfBeds?: number
  areaCoverage?: AreaCoverageOption
  constructibleAfter?: ConstructibleAfterOption
  locationClassificationType?: LocationClassificationTypeOption
  supplyType?: SupplyTypeOption
  utilizationTradeSite?: UsageTypeOption
  constructionPhase?: ConstructionPhaseOption
  rentalIncome?: number | string
  rented?: boolean
  auxiliaryArea?: number | string
  noBedrooms?: string
  priceMultiplier?: number | string
  rentalIncomeActual?: number | string
  rentalIncomeTarget?: number | string
  industrialArea?: number | string
  pricePerSQM?: number | string
  serviceCharge?: number | string
  additionalArea?: number | string
}

export type RealEstateLocation = {
  distanceToPT?: string
  distanceToMRS?: string
  distanceToFM?: string
  distanceToAirport?: string
}

export type RealEstateMetaInformation = {
  selectedMenuItem?: string
  entryMode?: string
}

export type RealEstateAddress = {
  id?: string
  city: string
  number?: string
  postcode?: string
  street?: string
  hideStreetAndStreetNumber: boolean
  geoCoordinates?: RealEstateGeoCoordinates
  manual?: RealEstateManualAddress
  addressEditable?: boolean
}

export type RealEstateManualAddress = {
  country: { id: number; name?: string }
  region: { id: number; name?: string }
  city: { id: number; name?: string }
  quarter: { id: number; name?: string; geoCoordinates?: GeoCoordinates }
  number?: string
  postcode?: string
  street?: string
}

export type Wgs84Coordinate = {
  longitude: number
  latitude: number
}

export interface RealEstateGeoCoordinates extends Wgs84Coordinate {
  lambertEuCoordinatesX?: number
  lambertEuCoordinatesY?: number
}

export type ApiSearchData = {
  searchField1?: string
  searchField2?: string
  searchField3?: string
}
export type RealEstateContact = {
  contactId?: number
  salutationType?: Salutation
  externalId?: string
  firstName?: string
  lastName?: string
  phoneNumber?: string
  email?: string
}

export type RealEstateAdditionalInformation = {
  descriptionNote?: string
  furnishingNote?: string
  locationNote?: string
  otherNote?: string
}

export enum RealEstatePriceTypeOption {
  RENT_PER_MONTH_PRICE = 'RENT_PER_MONTH_PRICE',
  RENT_PER_SQM_PRICE = 'RENT_PER_SQM_PRICE',
  REQUEST_RENT_PRICE = 'REQUEST_RENT_PRICE',
  PURCHASE_PRICE = 'PURCHASE_PRICE',
  REQUEST_PURCHASE_PRICE = 'REQUEST_PURCHASE_PRICE',
}

export enum RealEstatePricePeriodTypeOption {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
}

export type RealEstatePrice = {
  price?: number | string
  priceType?: RealEstatePriceTypeOption
  pricePeriodType?: RealEstatePricePeriodTypeOption
  monthlyRent?: string
  baseRent?: string
}

export type RealEstateOffer = {
  estate: RealEstate
}

export type RealEstateTenantDocuments = {
  solvency: boolean
  income: boolean
  rental: boolean
  selfReport: boolean
  identification: boolean
}

export interface RealEstateDTO {
  estate: RealEstate
  attachments: Attachment[]
  multimediaOperations: { multimediaToDelete: Attachment[]; multimediaToUpdate: Attachment[] }
}

export type ApiSiteRecommendedUseTypes = {
  NO_INFORMATION?: boolean
  FUTURE_DEVELOPMENT_LAND?: boolean
  TWINHOUSE?: boolean
  SINGLE_FAMILY_HOUSE?: boolean
  GARAGE?: boolean
  GARDEN?: boolean
  NO_DEVELOPMENT?: boolean
  APARTMENT_BUILDING?: boolean
  ORCHARD?: boolean
  TERRACE_HOUSE?: boolean
  PARKING_SPACE?: boolean
  VILLA?: boolean
  FORREST?: boolean
  FARMLAND?: boolean
  MOORAGE?: boolean
  OFFICE?: boolean
  CAMPING?: boolean
  BIG_STORE?: boolean
  LITTLE_STORE?: boolean
  GASTRONOMY?: boolean
  BUSINESS?: boolean
  HOTEL?: boolean
  INDUSTRY?: boolean
  SMALL_BUSINESS?: boolean
  STOCK?: boolean
  CAR_PARK?: boolean
  PRODUCTION?: boolean
}

export type MapImages = {
  fullSizeUrl: string
  mobileSizeUrl: string
}
